import { isTruckBodyStyle } from '@chukua/vehicle-model';
import type { PlacedTyre, VehicleLayout } from '@chukua/vehicle-model';

/** SVG units per metre. */
const SCALE = 100;
const PADDING = 30;
/** A run of chassis longer than this with no axle on it is drawn shorter, so a 40ft trailer's neck doesn't push its tyres off the chart. */
const UNBROKEN_GAP = 2.6;
const GAP_KEPT_BEYOND = 0.3;

export type PlanMaterial = 'steel' | 'paint';

export interface PlanRect {
  kind: 'rect';
  material: PlanMaterial;
  /** Along the chart, front on the right. */
  x: number;
  /** Across the chart, the driver's left side at the top. */
  y: number;
  width: number;
  height: number;
}

export interface PlanTyreShape extends PlacedTyre {
  kind: 'tyre';
  /** The tyre's footprint on the chart. */
  rect: { x: number; y: number; width: number; height: number };
}

export type PlanShape = PlanRect | PlanTyreShape;

export interface PlanScene {
  viewBox: { x: number; y: number; width: number; height: number };
  shapes: PlanShape[];
}

/** Maps a depth along the vehicle to chart units, shortening long spans with no axle on them. */
export function compressDepths(stops: number[]): (depth: number) => number {
  const sorted = [...new Set(stops)].sort((a, b) => a - b);
  const scaled = [0];
  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i] - sorted[i - 1];
    const kept = gap <= UNBROKEN_GAP ? gap : UNBROKEN_GAP + (gap - UNBROKEN_GAP) * GAP_KEPT_BEYOND;
    scaled.push(scaled[i - 1] + kept);
  }
  return (depth) => {
    const i = sorted.findIndex((stop, k) => k > 0 && depth <= stop);
    if (i <= 0) return depth <= sorted[0] ? 0 : scaled[scaled.length - 1];
    const t = (depth - sorted[i - 1]) / (sorted[i] - sorted[i - 1]);
    return scaled[i - 1] + t * (scaled[i] - scaled[i - 1]);
  };
}

function rect(material: PlanMaterial, along: [number, number], across: [number, number]): PlanRect {
  return { kind: 'rect', material, x: along[0], y: across[0], width: along[1] - along[0], height: across[1] - across[0] };
}

/**
 * The chart tyre fitters already know, from straight above (gitaumoses4/chukua#176):
 * the outline of the cab or deck and the frame, then the tyres on top. It
 * lies on its side so a long trailer fits a wide screen: front on the right,
 * the driver's left at the top, so each axle reads top to bottom in slot order.
 */
export function computePlanScene(layout: VehicleLayout): PlanScene {
  const toChart = compressDepths([0, ...layout.axles.flatMap((a) => [a.depth - 1, a.depth, a.depth + 1]), layout.length].filter((d) => d >= 0));
  const total = toChart(layout.length) * SCALE;
  const along = (depth: number) => total - toChart(depth) * SCALE;
  const across = (x: number) => x * SCALE;
  const span = (from: number, to: number): [number, number] => [along(to), along(from)];
  const isTruck = isTruckBodyStyle(layout.bodyStyle) && layout.kind === 'truck_class';

  const outline: PlanRect[] = [
    rect('steel', span(isTruck ? 0.35 : 0, layout.length), [across(-0.47), across(-0.39)]),
    rect('steel', span(isTruck ? 0.35 : 0, layout.length), [across(0.39), across(0.47)]),
  ];
  if (isTruck) outline.unshift(rect('paint', span(0, 2.2), [across(-1.23), across(1.23)]));
  if (isTruck && layout.bodyStyle !== 'tractor') outline.unshift(rect('paint', span(2.55, layout.length), [across(-1.25), across(1.25)]));
  if (!isTruck) outline.unshift(rect('paint', span(0, layout.length), [across(-1.25), across(1.25)]));

  const tyres: PlanTyreShape[] = layout.axles.flatMap((axle) =>
    axle.tyres.map((tyre) => ({
      ...tyre,
      kind: 'tyre' as const,
      rect: {
        x: along(axle.depth) - tyre.radius * SCALE,
        y: across(tyre.x) - (tyre.width * SCALE) / 2,
        width: 2 * tyre.radius * SCALE,
        height: tyre.width * SCALE,
      },
    })),
  );
  const halfWidth = 1.3 * SCALE;
  return { viewBox: { x: -PADDING, y: -halfWidth - PADDING, width: total + 2 * PADDING, height: 2 * halfWidth + 2 * PADDING }, shapes: [...outline, ...tyres] };
}
